import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { getTeacherRedemptionNotifications, markRedemptionNotificationsRead } from '../../services/rewardService';
import { supabase } from '../../lib/supabase';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [teacherDisplayName, setTeacherDisplayName] = useState('');

  const navigationItems = [
    { label: 'Dashboard', path: '/teacher-dashboard', icon: 'LayoutDashboard' },
    { label: 'Create Trail', path: '/learning-trails-creator', icon: 'PlusCircle' },
    { label: 'Classrooms', path: '/classroom-management', icon: 'Users' },
    { label: 'QR Codes', path: '/qr-code-generator', icon: 'QrCode' },
    { label: 'Rewards', path: '/teacher-rewards-management', icon: 'Star' },
  ];

  const secondaryItems = [
    { label: 'Settings', path: '/teacher-settings', icon: 'Settings' },
    { label: 'Student Login', path: '/student-login', icon: 'GraduationCap' },
  ];

  const [notifications, setNotifications] = useState([]);
  const realtimeChannelRef = useRef(null);
  const unreadCount = notifications?.filter(n => n?.isUnread)?.length;

  const isActive = (path) => {
    return location?.pathname === path;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleNotificationClick = (notification) => {
    navigate('/teacher-rewards-management');
    setNotificationOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/teacher-login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  // Fetch notifications from Supabase
  const fetchNotifications = async () => {
    if (!user?.id) return;
    const { notifications: data } = await getTeacherRedemptionNotifications(user?.id);
    setNotifications(data || []);
  };

  useEffect(() => {
    fetchNotifications();
  }, [user?.id]);

  useEffect(() => {
    const fetchTeacherName = async () => {
      if (!user?.id) return;
      const { data } = await supabase?.from('teachers')?.select('full_name')?.eq('id', user?.id)?.single();
      if (data?.full_name) {
        setTeacherDisplayName(data?.full_name);
      }
    };
    fetchTeacherName();
  }, [user?.id]);

  // Real-time subscription for new redemptions
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase?.channel(`teacher-redemptions-${user?.id}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'reward_redemptions'
        },
        () => {
          fetchNotifications();
        }
      )?.subscribe();

    realtimeChannelRef.current = channel;

    return () => {
      if (realtimeChannelRef?.current) {
        supabase?.removeChannel(realtimeChannelRef?.current);
      }
    };
  }, [user?.id]);

  const handleNotificationOpen = async () => {
    const opening = !notificationOpen;
    setNotificationOpen(opening);
    if (opening && user?.id && unreadCount > 0) {
      // Optimistically mark as read in UI
      setNotifications(prev => prev?.map(n => ({ ...n, isUnread: false })));
      // Persist to DB
      await markRedemptionNotificationsRead(user?.id);
    }
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[1000] bg-card shadow-md">
        <div className="flex items-center justify-between h-16 px-4 lg:px-6">
          <div className="flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 mr-2 rounded-md hover:bg-muted transition-organic active-press"
              aria-label="Toggle mobile menu"
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>

            <div 
              className="flex items-center cursor-pointer hover-lift"
              onClick={() => handleNavigation('/teacher-dashboard')}
            >
              <div className="header-logo">
                <Icon name="BookOpen" size={24} className="header-logo-icon" />
              </div>
              <span className="header-logo-text">LearningTrails</span>
            </div>
          </div>

          <nav className="hidden lg:flex items-center space-x-2">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-organic hover-lift active-press ${
                  isActive(item?.path)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={item?.icon} size={18} className="mr-2" />
                {item?.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center space-x-2">
            <div className="relative">
              <button
                onClick={handleNotificationOpen}
                className="relative p-2 rounded-md hover:bg-muted transition-organic active-press"
                aria-label="Notifications"
              >
                <Icon name="Bell" size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 text-xs font-medium text-white bg-error rounded-full">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notificationOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[1010]"
                    onClick={() => setNotificationOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-popover rounded-lg shadow-lg z-[1020] overflow-hidden">
                    <div className="p-4 border-b border-border flex items-center justify-between">
                      <h3 className="font-semibold text-popover-foreground">Reward Redemptions</h3>
                      {notifications?.length > 0 && (
                        <span className="text-xs text-muted-foreground">{notifications?.length} total</span>
                      )}
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications?.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                          <Icon name="Bell" size={32} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No redemptions yet</p>
                        </div>
                      ) : (
                        notifications?.map((notification) => (
                          <button
                            key={notification?.id}
                            onClick={() => handleNotificationClick(notification)}
                            className={`w-full p-4 text-left border-b border-border cursor-pointer transition-colors hover:bg-muted/60 ${
                              notification?.isUnread ? 'bg-primary/5 hover:bg-primary/10' : ''
                            }`}
                          >
                            <div className="flex items-start">
                              <div className="w-8 h-8 rounded-full bg-warning/10 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                                <Icon name="Gift" size={16} className="text-warning" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-popover-foreground truncate">
                                  {notification?.studentName}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  Redeemed <span className="font-medium text-foreground">{notification?.rewardTitle}</span>
                                </p>
                                <div className="flex items-center mt-1 space-x-2">
                                  <span className="text-xs text-warning flex items-center">
                                    <Icon name="Star" size={10} className="mr-0.5" />
                                    {notification?.starsSpent} stars
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {notification?.redeemedAt
                                      ? new Date(notification?.redeemedAt)?.toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })
                                      : ''}
                                  </span>
                                </div>
                              </div>
                              {notification?.isUnread && (
                                <div className="w-2 h-2 bg-primary rounded-full ml-2 mt-2 flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                    <div className="p-3 border-t border-border">
                      <Button
                        variant="ghost"
                        size="sm"
                        fullWidth
                        onClick={() => {
                          navigate('/teacher-rewards-management');
                          setNotificationOpen(false);
                        }}
                      >
                        Manage Rewards
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className="flex items-center p-2 rounded-md hover:bg-muted transition-organic active-press"
                aria-label="Profile menu"
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Icon name="User" size={18} className="text-primary" />
                </div>
              </button>

              {profileMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[1010]"
                    onClick={() => setProfileMenuOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-2 w-56 bg-popover rounded-lg shadow-lg z-[1020] overflow-hidden">
                    <div className="p-4 border-b border-border">
                      {teacherDisplayName && (
                        <p className="text-sm font-bold text-popover-foreground truncate mb-0.5">
                          {teacherDisplayName}
                        </p>
                      )}
                      <p className="text-sm font-medium text-popover-foreground truncate">
                        {user?.email || 'Teacher'}
                      </p>
                    </div>
                    <div className="p-2">
                      <button
                        onClick={() => {
                          handleNavigation('/teacher-settings');
                          setProfileMenuOpen(false);
                        }}
                        className="w-full flex items-center px-3 py-2 rounded-md text-sm hover:bg-muted transition-organic"
                      >
                        <Icon name="Settings" size={18} className="mr-3" />
                        Settings
                      </button>
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center px-3 py-2 rounded-md text-sm text-error hover:bg-error/10 transition-organic"
                      >
                        <Icon name="LogOut" size={18} className="mr-3" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
      {mobileMenuOpen && (
        <>
          <div
            className="fixed inset-0 bg-background z-[1010] lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
          <nav className="fixed top-16 left-0 right-0 bottom-0 bg-card z-[1020] lg:hidden overflow-y-auto">
            <div className="p-4 space-y-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full flex items-center px-4 py-3 rounded-md text-sm font-medium transition-organic active-press ${
                    isActive(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} className="mr-3" />
                  {item?.label}
                </button>
              ))}
              <div className="pt-4 border-t border-border">
                {secondaryItems?.map((item) => (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path)}
                    className={`w-full flex items-center px-4 py-3 rounded-md text-sm font-medium transition-organic active-press ${
                      isActive(item?.path)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} className="mr-3" />
                    {item?.label}
                  </button>
                ))}
              </div>
            </div>
          </nav>
        </>
      )}
    </>
  );
};

export default Header;